// React Dependencies
import React, { useEffect, useState } from 'react';

// External Dependencies
import moment from 'moment';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Paper, FormControl, RadioGroup, FormControlLabel, Radio, Tooltip } from '@material-ui/core';

// Internal Dependencies
import MaterialDatePicker from '../../../../components/Common/form/MaterialDatePicker';
import Colours from '../../../../styles/colours';
import { IsEmpty } from '../../../../common/util';
import { Status } from '../../../../common/constants';
import PostAction from '../../../../actions/post';
import PromoCodes from './PromoCodes';
import PostCategories from './PostCategories';
import postStyle from '../../../../styles/post';
import Tags from './Tags';
import MText from '../../../../components/Common/MText';
import MTextField from '../../../../components/MTextField';

const Details = (props) => {
  const [title, setTitle] = useState('');

  const [promoPeriodRangeStatus, setPromoPeriodRangeStatus] = useState(false);
  const [noSpecPeriodStatus, setNoSpecPeriodStatus] = useState(true);
  const [promoFromDate, setPromoFromDate] = useState('');
  const [promoTillDate, setPromoTillDate] = useState('');
  const [description, setDescription] = useState('');
  const [promoTerms, setPromoTerms] = useState('');
  const [disableInputFields, setDisableInputFields] = useState(false);

  useEffect(() => {
    if (!IsEmpty(props?.post)) {
      const p = props.post;
      setTitle(p.title || '');
      setPromoFromDate(p.promoFromDate || '');
      setPromoTillDate(p.promoTillDate || '');
      setDescription(p.description || '');
      setPromoTerms(p.promoTerms || '');

      if (!IsEmpty(p.promoFromDate) || !IsEmpty(p.promoTillDate)) {
        setPromoPeriodRangeStatus(true);
        setNoSpecPeriodStatus(false);
      }

      setDisableInputFields([Status.Live, Status.Paused].includes(props.post.currentPostStatus));
    }
  }, [props.post]);

  return (
    <>
      <Paper variant="elevation" className="px-5" style={postStyle.paper}>
        <MText style={postStyle.textHeader} text="Title">
          <sup style={{ color: Colours.Red }}>*</sup>
        </MText>

        <MTextField
          className="w-100 mb-3 mt-2"
          value={title || ''}
          onChange={(text) => props.dispatchPostUpdate({ title: text })}
        />

        <MText className="mt-1 mb-1" style={postStyle.textHeader}>
          Period
          <sup style={{ color: Colours.Red }}>*</sup>
        </MText>

        { ![Status.Live, Status.Pending].includes(props.post.currentPostStatus) ? (
          <FormControl component="fieldset" className="pl-4">
            <RadioGroup aria-label="promo period" name="customized-radios">
              <FormControlLabel
                control={(
                  <>
                    <Radio checked={promoPeriodRangeStatus} color="primary" size="small" disabled={disableInputFields} />

                    <MaterialDatePicker
                      disabled={disableInputFields}
                      minDate={false}
                      maxDate={IsEmpty(promoTillDate) ? undefined : new Date(promoTillDate)}
                      onChange={(date) => {
                        const newDate = !IsEmpty(date) ? new Date(date).toString('yyyy-MM-dd') : null;
                        props.dispatchPostUpdate({ promoFromDate: newDate });
                      }}
                      value={IsEmpty(promoFromDate) ? null : new Date(promoFromDate)}
                      readOnly={!promoPeriodRangeStatus}
                      label="Starts on"
                      size="small"
                      variant="outlined"
                      style={{ marginLeft: 10, marginRight: 20 }}
                    />

                    <MaterialDatePicker
                      disabled={disableInputFields}
                      minDate={IsEmpty(promoFromDate) ? false : new Date(promoFromDate)}
                      onChange={(date) => {
                        const newDate = !IsEmpty(date) ? new Date(date).toString('yyyy-MM-dd') : null;
                        props.dispatchPostUpdate({ promoTillDate: newDate });
                      }}
                      value={IsEmpty(promoTillDate) ? null : new Date(promoTillDate)}
                      readOnly={!promoPeriodRangeStatus}
                      label="Ends on (inclusive)"
                      size="small"
                      variant="outlined"
                    />
                  </>
                )}
                onClick={() => {
                  if (!disableInputFields) {
                    setPromoPeriodRangeStatus(true);
                    setNoSpecPeriodStatus(false);
                  }
                }}
              />

              <FormControlLabel
                control={(
                  <>
                    <Radio checked={noSpecPeriodStatus} color="primary" size="small" disabled={disableInputFields} />
                    <MText className="ml-2" text="No specific period" size={12} />
                  </>
                )}
                onClick={() => {
                  if (!disableInputFields) {
                    props.dispatchPostUpdate({ promoFromDate: null, promoTillDate: null });
                    setNoSpecPeriodStatus(true);
                    setPromoPeriodRangeStatus(false);
                  }
                }}
              />
            </RadioGroup>
          </FormControl>
        ) : (
          <>
            <div className="alert alert-warning" role="alert">
              <MText text="Promo period can only be modified when it is in Draft status" />
              <hr />
              { props.post.promoFromDate && props.post.promoTillDate ? (
                <div>
                  <MText
                    bold
                    size={13}
                    text={`Promo starts: ${moment(new Date(props.post.promoFromDate)).utcOffset(+8, false).format('YYYY-MM-DD')}`}
                  />
                  <MText
                    bold
                    size={13}
                    text={`Promo starts: ${moment(new Date(props.post.promoTillDate)).utcOffset(+8, false).format('YYYY-MM-DD')}`}
                  />
                </div>
              ) : <MText size={11} text="Promo period not specified" />}
            </div>
          </>
        ) }

        <MText className="mt-2 mb-2" style={postStyle.textHeader}>
          Description
        </MText>

        <MTextField
          className="w-100 mb-2"
          multiline
          minRows={4}
          maxRows={10}
          value={description || ''}
          onChange={(text) => props.dispatchPostUpdate({ description: text })}
        />

        <hr className="my-4" />

        <MText className="mt-4 mb-2" style={postStyle.textHeader}>
          Categories
          <sup style={{ color: Colours.Red }}>*</sup>
        </MText>
        <PostCategories disabled={disableInputFields} disabledMsg={`Cannot be updated because post is ${props.post.currentPostStatus}`} />

        <hr className="my-4" />
        <MText className="mt-4 mb-2" style={postStyle.textHeader}>
          Promo Codes
        </MText>
        <PromoCodes disabled={disableInputFields} disabledMsg={`Cannot be updated because post is ${props.post.currentPostStatus}`} />

        <MText className="mt-4 mb-2" style={postStyle.textHeader}>
          Terms &amp; Conditions
        </MText>
        <Tooltip
          title={disableInputFields ? `Cannot be updated because post is ${props.post.currentPostStatus}` : ''}
          isLink
        >
          <MTextField
            className="w-100"
            multiline
            minRows={4}
            maxRows={8}
            value={promoTerms || ''}
            onChange={(text) => props.dispatchPostUpdate({ promoTerms: text })}
            disabled={disableInputFields}
          />
        </Tooltip>

        <Tags className="mt-4" disabled={disableInputFields} disabledMsg={`Cannot be updated because post is ${props.post.currentPostStatus}`} />
      </Paper>
    </>
  );
};

Details.propTypes = {
  post: PropTypes.shape({
    status:            PropTypes.string,
    currentPostStatus: PropTypes.string,
    title:             PropTypes.string,
    promoFromDate:     PropTypes.string,
    promoTillDate:     PropTypes.string,
    description:       PropTypes.string,
    promoTerms:        PropTypes.string,
  }).isRequired,
  dispatchPostUpdate: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  post: state.post.selected,
});

const mapDispatchToProps = (dispatch) => ({
  dispatchPostUpdate: (post) => PostAction.postDataUpdate(dispatch, post),
});

export default connect(mapStateToProps, mapDispatchToProps)(Details);
